// shell/RouteTransition.tsx — wraps the in-shell routed Outlet content in a subtle GSAP enter
// transition (cross-fade + ~12px rise) on each navigation. No-op under reduced motion
// (app pref OR OS). Mirrors features/auth/useAuthEntrance. In-shell pages only; BareLayout untouched.

import { useLayoutEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { useLocation } from 'react-router-dom';
import { usePreferences } from '@/hooks';
import { prefersReducedMotion } from './Backdrop/prefersReducedMotion';

// Defensive min-width:0 so an unforeseen intrinsic-width child can't force horizontal overflow.
const Wrap = styled.div`
  min-width: 0;
`;

export function RouteTransition({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const { prefs } = usePreferences();
  const motionOff = prefs.reduceMotion || prefersReducedMotion();
  const { pathname } = useLocation();

  // Keyed on [pathname, motionOff]: a new route — or a motion-pref change — re-runs this.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (motionOff) {
      // Motion off (pref or OS): ensure content is fully visible. Covers the case where a
      // prior in-flight tween was killed by cleanup when the pref toggled on mid-fade.
      gsap.set(el, { autoAlpha: 1, y: 0 });
      return;
    }
    const tween = gsap.fromTo(
      el,
      { autoAlpha: 0, y: 12 },
      { autoAlpha: 1, y: 0, duration: 0.35, ease: 'power2.out' },
    );
    return () => {
      tween.kill();
    };
  }, [pathname, motionOff]);

  return <Wrap ref={ref}>{children}</Wrap>;
}
